import { useRef } from "react";
import { Link } from "react-router-dom";
import { useBlogData } from "@/hooks/useBlogData";
import { ChevronLeft, ChevronRight, BookOpen } from "lucide-react";
import { motion } from "framer-motion";

const HomeBlogs = () => {
  const { data: allBlogs } = useBlogData();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Show all published blogs, featured ones first (newest first within each group)
  const blogs = [...(allBlogs ?? [])]
    .sort((a, b) => Number(b.is_featured) - Number(a.is_featured))
    .slice(0, 12);

  if (!blogs.length) return null;

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth * 0.8;
    scrollRef.current.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  return (
    <section className="py-12 relative">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-fredoka font-bold text-gray-800 inline-flex items-center gap-3"
          >
            <BookOpen className="w-9 h-9 text-cyan-600" />
            Our Blogs
          </motion.h2>
        </div>

        <div className="relative">
          {/* Arrows */}
          <button
            onClick={() => scroll("left")}
            aria-label="Previous blogs"
            className="hidden md:flex absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white shadow-lg items-center justify-center hover:bg-cyan-50 hover:scale-110 transition-all"
          >
            <ChevronLeft className="w-6 h-6 text-cyan-600" />
          </button>
          <button
            onClick={() => scroll("right")}
            aria-label="Next blogs"
            className="hidden md:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white shadow-lg items-center justify-center hover:bg-cyan-50 hover:scale-110 transition-all"
          >
            <ChevronRight className="w-6 h-6 text-cyan-600" />
          </button>

          {/* Scrollable cards */}
          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 scrollbar-hide -mx-4 px-4"
            style={{ scrollbarWidth: "none" }}
          >
            {blogs.map((blog, i) => (
              <motion.div
                key={blog.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="snap-start flex-shrink-0 w-[280px] sm:w-[320px] bg-cyan-50 rounded-3xl border-2 border-cyan-200 shadow-md hover:shadow-xl transition-all p-4 flex flex-col"
              >
                <div className="rounded-2xl overflow-hidden h-44 mb-4 bg-cyan-100">
                  {blog.image_url ? (
                    <img
                      src={blog.image_url}
                      alt={blog.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-5xl">📚</div>
                  )}
                </div>
                <h3 className="font-fredoka font-bold text-lg md:text-xl text-gray-800 mb-2 line-clamp-2 leading-snug">
                  {blog.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 line-clamp-3 mb-4 flex-grow leading-relaxed">
                  {blog.short_description}
                </p>
                <Link
                  to={`/blog/${blog.slug}`}
                  className="inline-block bg-indigo-900 hover:bg-indigo-800 text-white font-fredoka font-semibold text-sm px-5 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all self-start"
                >
                  Read More
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="text-center mt-6">
          <Link
            to="/blog"
            className="inline-block bg-amber-400 hover:bg-amber-500 text-gray-900 font-fredoka font-bold px-6 py-2.5 rounded-full shadow-md hover:shadow-lg transition-all"
          >
            View All Blogs ({allBlogs?.length ?? 0}) →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default HomeBlogs;
